import { CommonModule } from '@angular/common';
import {
  Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { RouterModule } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  Subject
} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import {
  EmptyURIValue,
  encodeSpecialCharacters,
  URIPrefix,
} from '@sondermind/data-access/directory-link';
import {
  BrowseLinkKeys, BrowseLinkKeyType, IAPIListRequestOptions, IAPIListResponse, IBrowseLinkApiResponse, IBrowseLinkData
} from '@sondermind/utilities/models-http';

import { BrowseStateHttpService } from '@sondermind/utilities/services';

@Component({
  imports: [
    CommonModule,
    RouterModule,
  ],
  standalone: true,
  selector: 'browse-therapists-card',
  templateUrl: './browse-therapists-card.component.html',
  styleUrls: ['./browse-therapists-card.component.scss'],
})
export class BrowseTherapistsCard implements OnInit, OnChanges {
  @Input() currentStateAbbrev!: string;
  @Input() selectedCity?: string;

  destroyed$ = new Subject<void>();
  emptyURIValue = EmptyURIValue;
  uriPrefix = URIPrefix;
  providerDirectoryUrl: string = '';
  uriSafeCity$ = new BehaviorSubject<string>('');

  cities$ = new BehaviorSubject<IBrowseLinkData[]>([]);
  insurances$ = new BehaviorSubject<IBrowseLinkData[]>([]);
  specialities$ = new BehaviorSubject<IBrowseLinkData[]>([]);

  combinedBrowseLinks$ = combineLatest([
    this.specialities$,
    this.insurances$,
    this.cities$
  ]).pipe(
    map(([specialities, insurances, cities]) => ({ specialities, insurances, cities }))
  );

  constructor(
    private browseStateHttpService: BrowseStateHttpService,
    private configurationService: ConfigurationService
  ) {}

  ngOnInit(): void {
    this.providerDirectoryUrl =
    this.configurationService.env.providerDirectoryBase ||
    'https://www.sondermind.com/provider-directory';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const stateChange = changes['currentStateAbbrev'];
    const cityChange = changes['selectedCity'];

    if (stateChange?.currentValue) {
      this.currentStateAbbrev = stateChange?.currentValue;
    }

    if (cityChange?.currentValue) {
      this.selectedCity = cityChange?.currentValue;

      const newCity = cityChange?.currentValue;
      const isValidCity = newCity && newCity !== this.emptyURIValue;
      this.uriSafeCity$.next(
        isValidCity
          ? encodeURIComponent(encodeSpecialCharacters(newCity.toLocaleLowerCase()))
          : ''
      );
    }

    if (stateChange?.currentValue || cityChange?.currentValue) {
      this.loadBrowseData();
    }
  }

  private loadBrowseData(): void {
    const stateOptions: IAPIListRequestOptions = {
      page: 'disabled',
      filter: { key: 'state', value: this.currentStateAbbrev },
    };

    const stateLinks$ = this.browseStateHttpService.getBrowseLinks(stateOptions);

    if (this.uriSafeCity$.getValue() && this.selectedCity) {
      const cityOptions: IAPIListRequestOptions = {
        page: 'disabled',
        filter: [
          { key: 'state', value: this.currentStateAbbrev },
          { key: 'city', value: this.selectedCity }
        ],
      };

      const cityLinks$ = this.browseStateHttpService.getBrowseCityLinks(cityOptions);

      forkJoin({ stateData: stateLinks$, cityData: cityLinks$ })
        .pipe(takeUntil(this.destroyed$))
        .subscribe(({ stateData, cityData }: {
          stateData: IAPIListResponse<IBrowseLinkApiResponse>;
          cityData: IAPIListResponse<IBrowseLinkApiResponse>;
        }) => {
          this.cities$.next(this.getTopItems(stateData.data, BrowseLinkKeys.CITY, 5));
          this.insurances$.next(this.getTopItems(cityData.data, BrowseLinkKeys.INSURANCE, 5));
          this.specialities$.next(this.getTopItems(cityData.data, BrowseLinkKeys.SPECIALTY, 5));
        });
    } else {
      stateLinks$
        .pipe(takeUntil(this.destroyed$))
        .subscribe(({ data }: IAPIListResponse<IBrowseLinkApiResponse>) => {
          this.cities$.next(this.getTopItems(data, BrowseLinkKeys.CITY, 5));
          this.insurances$.next(this.getTopItems(data, BrowseLinkKeys.INSURANCE, 5));
          this.specialities$.next(this.getTopItems(data, BrowseLinkKeys.SPECIALTY, 5));
        });
    }
  }

  // Filter then sort by provider_count in descending order
  // And slice given count
  private getTopItems(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType,
    count: number,
  ): IBrowseLinkData[] {
    return items
      .filter((item) => item[key as keyof IBrowseLinkApiResponse] != null)
      .sort((a, b) => {
        const countDiff = b.provider_count - a.provider_count;
        if (countDiff !== 0) return countDiff;

        const aValue = (a[key as keyof IBrowseLinkApiResponse] ?? '').toString().toLowerCase();
        const bValue = (b[key as keyof IBrowseLinkApiResponse] ?? '').toString().toLowerCase();
        return aValue.localeCompare(bValue);
      })
      .slice(0, count)
      .map((item) => {
        const value = item[key as keyof IBrowseLinkApiResponse] as string;
        return {
          value,
          uriSafeValue: encodeURIComponent(encodeSpecialCharacters(value.toLocaleLowerCase()))
        };
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
